export const REPORT_TABLE_FIELD_ORDER = [
  "customId",
  "name",
  "dimension",
  "maturity",
  "owner",
  "dropdownFieldOne",
  "status",
  "degreeOfCompletion",
  "progress",
  "budget",
  "startDate",
  "endDate",
] as const;

// Map a field name to its canonical column index used by row data arrays.
export const getReportFieldOrderIndex = (fieldName: string): number => {
  const index = REPORT_TABLE_FIELD_ORDER.indexOf(
    fieldName as (typeof REPORT_TABLE_FIELD_ORDER)[number]
  );
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

// Apply display order safely, keeping all headers even if the order is stale.
export const applyColumnOrderToHeadersForReportPlugin = <T>(
  headers: T[],
  colOrder?: number[]
): T[] => {
  if (!colOrder || colOrder.length === 0) {
    return headers;
  }

  const validOrdered = colOrder
    .filter(
      (index) => Number.isInteger(index) && index >= 0 && index < headers.length
    )
    .map((index) => headers[index]);

  if (validOrdered.length === headers.length) {
    return validOrdered;
  }

  const seen = new Set(validOrdered);
  const missing = headers.filter((header) => !seen.has(header));
  return [...validOrdered, ...missing];
};

// Rebuild UI column order from persisted header sequence (by field name).
export const getColumnOrderFromHeadersForReportPlugin = (
  headers: { fieldName: string }[] | undefined
): number[] | undefined => {
  if (!headers || headers.length === 0) {
    return undefined;
  }

  const baseIndexByFieldName = new Map<string, number>(
    REPORT_TABLE_FIELD_ORDER.map((fieldName, index) => [fieldName, index])
  );

  const ordered = headers
    .map((header) => baseIndexByFieldName.get(header.fieldName))
    .filter((index): index is number => index !== undefined);

  if (ordered.length !== REPORT_TABLE_FIELD_ORDER.length) {
    return undefined;
  }

  return ordered;
};

// Keep render inputs in canonical order; PMTable applies user order via colOrder.
export const sortHeadersByBaseFieldOrderForReportPlugin = <
  T extends { fieldName: string },
>(
  headers: T[]
): T[] => {
  return [...headers].sort(
    (a, b) =>
      getReportFieldOrderIndex(a.fieldName) -
      getReportFieldOrderIndex(b.fieldName)
  );
};
